import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Maximize2, Minimize2, RotateCcw, ArrowLeft } from "lucide-react";
import { InlineConcierge } from "@/components/concierge/InlineConcierge";
import { DrawerBrowsePanel } from "@/components/concierge/DrawerBrowsePanel";
import { ProductCheckoutModals } from "@/components/product-detail/ProductCheckoutModals";
import { useProductCheckoutFlow } from "@/hooks/useProductCheckoutFlow";
import {
  useFloatingConciergeUi,
} from "@/contexts/FloatingConciergeContext";
import { FLOATING_CONCIERGE_SESSION_KEY } from "@/lib/floatingConciergeStorage";
import {
  FLOATING_LAUNCHER_SIZE_CLASS,
  FLOATING_PANEL_HEIGHT_CLASS,
  FLOATING_PANEL_HEIGHT_EXPANDED_CLASS,
  FLOATING_PANEL_MOBILE_WIDTH_CLASS,
  FLOATING_PANEL_WIDTH_CLASS,
  FLOATING_PANEL_WIDTH_EXPANDED_CLASS,
} from "@/components/concierge/floatingConciergeLayout";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useProfile, useCompanyAddresses, useTeamMembers } from "@/hooks/queries";
import { pickDefaultCompanyAddress } from "@/lib/pickDefaultCompanyAddress";
import { celebrationDateOnly } from "@/lib/orderConfirmPrefill";
import { parseResolvedBriefSnapshot } from "@/lib/floatingCelebrationCheckout";
import { recipientToInitialBrief } from "@/components/concierge/DashboardConciergeDrawer";
import {
  PersonalizationHintsStrip,
  pickPersonalizationHints,
} from "@/components/concierge/PersonalizationHintsStrip";
import { humaniseOccasionShort } from "@/lib/humaniseOccasion";
import { useFloatingConciergeDeepLink } from "@/hooks/useFloatingConciergeDeepLink";

/**
 * Intercom-style floating concierge for the authed dashboard.
 *
 * Layout spec: docs/concierge-widget-ux-spec.md
 * - Persists one conversation across route changes (session in
 *   `lembra:floating_concierge_session_id`, separate from landing hero).
 * - Stays mounted while closed so history survives collapse/navigation.
 * - Hidden on /settings, /chat, /billing; visible on /dashboard and
 *   everywhere else under ConciergeLayout.
 * - Suppressed when DashboardConciergeDrawer or checkout modals own
 *   the concierge surface.
 * - When celebrationContext is pinned, checkout prefill matches the
 *   adjust pipeline (recipient, occasion, date, address).
 * - Embedded browse overlay — never navigates to /browse on authed routes.
 */

const HIDDEN_ON_PATHS = ["/settings", "/chat", "/billing"];

function dollars(cents: number) {
  return `$${(cents / 100).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
}

export function FloatingConcierge() {
  const location = useLocation();
  useFloatingConciergeDeepLink();
  const {
    panelOpen,
    setPanelOpen,
    panelExpanded,
    setPanelExpanded,
    togglePanelExpanded,
    isSuppressed,
    celebrationContext,
    setCelebrationContext,
    adjustRecommendationSummary,
    setAdjustRecommendationSummary,
    deepLinkInitialBrief,
    conciergeMountGeneration,
    dashboardClientActions,
    teamClientActions,
  } = useFloatingConciergeUi();

  const [browseOpen, setBrowseOpen] = useState(false);

  const { user } = useAuth();
  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;
  const addressesQuery = useCompanyAddresses(companyId);
  const teamMembersQuery = useTeamMembers(companyId);

  const teamMemberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of teamMembersQuery.data ?? []) {
      map.set(member.id, member.name);
    }
    return map;
  }, [teamMembersQuery.data]);

  const checkoutDefaultAddress = useMemo(
    () => pickDefaultCompanyAddress(addressesQuery.data),
    [addressesQuery.data],
  );

  const isBrowseSurface = location.pathname.startsWith("/browse");

  const browseCheckout = useProductCheckoutFlow({
    cameFrom: isBrowseSurface ? "browse" : "dashboard",
  });
  const { resetCheckout: resetBrowseCheckout, handleViewCard: handleBrowseViewCard } =
    browseCheckout;

  const pathHidden = HIDDEN_ON_PATHS.some((p) =>
    location.pathname.startsWith(p),
  );
  const launcherHidden = pathHidden || isSuppressed;

  useEffect(() => {
    if (isSuppressed && panelOpen) setPanelOpen(false);
  }, [isSuppressed, panelOpen, setPanelOpen]);

  useEffect(() => {
    if (!panelOpen || launcherHidden) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (browseOpen) {
          setBrowseOpen(false);
          return;
        }
        setPanelOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [panelOpen, launcherHidden, setPanelOpen, browseOpen]);

  useEffect(() => {
    resetBrowseCheckout();
    setBrowseOpen(false);
  }, [celebrationContext?.recipientId]); // resetBrowseCheckout intentionally omitted — stable across renders

  const handleRecommendationCardsCount = useCallback((count: number) => {
    if (count >= 2) setPanelExpanded(true);
  }, [setPanelExpanded]);

  const handleResolvedBrief = useCallback(
    (brief: unknown) => {
      const parsed = parseResolvedBriefSnapshot(brief);
      if (!parsed?.recipient_id) return;
      if (celebrationContext?.recipientId === parsed.recipient_id) return;

      setCelebrationContext({
        recipientId: parsed.recipient_id,
        recipientName:
          teamMemberNameById.get(parsed.recipient_id) ??
          celebrationContext?.recipientName ??
          "",
        occasion: parsed.occasion ?? undefined,
        celebrationId: parsed.celebration_id ?? undefined,
        celebrationDate: parsed.delivery_date ?? undefined,
      });
    },
    [celebrationContext, setCelebrationContext, teamMemberNameById],
  );

  const shellResetRef = useRef<(() => void) | null>(null);

  const initialBrief = useMemo(() => {
    if (deepLinkInitialBrief) return deepLinkInitialBrief;
    if (celebrationContext) {
      return recipientToInitialBrief({
        id: celebrationContext.recipientId,
        name: celebrationContext.recipientName,
        occasion: celebrationContext.occasion,
        celebrationId: celebrationContext.celebrationId,
        celebrationDate: celebrationContext.celebrationDate,
      });
    }
    return { raw_text: "" };
  }, [celebrationContext, deepLinkInitialBrief]);

  const inlineConciergeKey =
    conciergeMountGeneration > 0
      ? `floating-${conciergeMountGeneration}`
      : "floating-persistent";

  const hasCelebrationCheckout =
    Boolean(celebrationContext?.recipientId) && Boolean(user?.id);

  const occasionLabel = celebrationContext?.occasion
    ? humaniseOccasionShort(celebrationContext.occasion)
    : null;

  const checkoutDefaultDate = celebrationDateOnly(celebrationContext?.celebrationDate);

  const personalizationHints = useMemo(
    () =>
      pickPersonalizationHints(teamMembersQuery.data, celebrationContext?.recipientId),
    [teamMembersQuery.data, celebrationContext?.recipientId],
  );

  const activeDraftScope = useMemo(() => {
    if (!user?.id || !celebrationContext?.recipientId) return null;
    return {
      user_id: user.id,
      recipient_id: celebrationContext.recipientId,
      ...(celebrationContext.celebrationId
        ? { celebration_id: celebrationContext.celebrationId }
        : {}),
    };
  }, [user?.id, celebrationContext]);

  const loadActiveDraftFor = useMemo(() => {
    if (!user?.id || !celebrationContext?.recipientId) return null;
    return {
      user_id: user.id,
      recipient_id: celebrationContext.recipientId,
    };
  }, [user?.id, celebrationContext?.recipientId]);

  if (pathHidden) return null;

  const showPanel = panelOpen && !isSuppressed;

  const browseOverlay =
    browseOpen &&
    celebrationContext &&
    typeof document !== "undefined"
      ? createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Browse gifts for ${celebrationContext.recipientName}`}
            data-testid="floating-browse-overlay"
            className="fixed inset-0 z-[60] flex flex-col bg-cream"
          >
            <div
              className="absolute inset-0 bg-navy/30 backdrop-blur-[2px]"
              aria-hidden="true"
              onClick={() => setBrowseOpen(false)}
            />
            <div className="relative z-10 mx-auto flex min-h-0 w-full max-w-[min(96vw,720px)] flex-1 flex-col overflow-hidden rounded-t-2xl bg-cream shadow-2xl md:my-4 md:rounded-2xl">
              <header
                className="flex shrink-0 items-center justify-between gap-3 border-b bg-white px-4 py-3"
                style={{ borderColor: "rgba(29,52,97,0.08)" }}
              >
                <p className="text-sm font-semibold text-navy">
                  Browse for {celebrationContext.recipientName}
                  {occasionLabel ? ` · ${occasionLabel}` : ""}
                </p>
                <button
                  type="button"
                  onClick={() => setBrowseOpen(false)}
                  data-testid="floating-browse-back"
                  className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:border-coral hover:text-coral"
                >
                  <ArrowLeft size={14} />
                  Chat
                </button>
              </header>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <DrawerBrowsePanel
                  recipientName={celebrationContext.recipientName}
                  recipientId={celebrationContext.recipientId}
                  occasion={celebrationContext.occasion}
                  onViewCard={handleBrowseViewCard}
                  layout="fullscreen"
                />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {!launcherHidden && !panelOpen && (
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          aria-label="Open Lembra concierge chat"
          className={cn(
            "fixed z-40 flex items-center justify-center rounded-full bg-coral text-white shadow-[0_10px_28px_-8px_rgba(196,86,74,0.55)] transition-transform hover:scale-105 hover:bg-coral-deep focus:outline-none focus:ring-2 focus:ring-coral/40 focus:ring-offset-2 md:right-6",
            isBrowseSurface ? "right-4 bottom-24 md:bottom-28" : "right-4 md:right-6",
            FLOATING_LAUNCHER_SIZE_CLASS,
          )}
          style={{
            background: "#C4564A",
            bottom: isBrowseSurface
              ? undefined
              : "max(env(safe-area-inset-bottom), 16px)",
          }}
        >
          <MessageCircle size={22} strokeWidth={2} />
        </button>
      )}

      {showPanel && (
        <div
          onClick={() => setPanelOpen(false)}
          className="fixed inset-0 z-40 bg-navy/20 backdrop-blur-[2px] md:hidden"
          aria-hidden="true"
        />
      )}

      <div
        role="dialog"
        aria-modal={showPanel}
        aria-label="Lembra concierge chat"
        aria-hidden={!showPanel}
        data-testid="floating-concierge-panel"
        className={cn(
          "fixed z-50 flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transition-[width,height,opacity] duration-200",
          showPanel
            ? cn(
                "pointer-events-auto opacity-100",
                FLOATING_PANEL_MOBILE_WIDTH_CLASS,
                "left-4 right-4 md:left-auto md:right-6",
                panelExpanded
                  ? FLOATING_PANEL_WIDTH_EXPANDED_CLASS
                  : FLOATING_PANEL_WIDTH_CLASS,
                panelExpanded
                  ? FLOATING_PANEL_HEIGHT_EXPANDED_CLASS
                  : FLOATING_PANEL_HEIGHT_CLASS,
              )
            : "pointer-events-none opacity-0 w-px h-px overflow-hidden -left-[9999px] bottom-0",
        )}
        style={
          showPanel
            ? { bottom: "max(env(safe-area-inset-bottom), 16px)" }
            : undefined
        }
      >
        <div className="flex shrink-0 items-center justify-between border-b border-charcoal/10 bg-cream px-4 py-3">
          <div className="min-w-0">
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.18em] text-coral/85"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              Concierge
            </p>
            {celebrationContext ? (
              <p
                className="truncate text-[15px] font-medium text-navy"
                style={{ fontFamily: "Newsreader, Georgia, serif", fontStyle: "italic" }}
                data-testid="floating-celebration-chip"
              >
                {celebrationContext.recipientName}
                {occasionLabel ? `'s ${occasionLabel}` : ""}
              </p>
            ) : (
              <p
                className="text-[15px] font-medium text-navy"
                style={{ fontFamily: "Newsreader, Georgia, serif", fontStyle: "italic" }}
              >
                How can we help?
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {celebrationContext && (
              <button
                type="button"
                onClick={() => setBrowseOpen(true)}
                data-testid="floating-browse-trigger"
                className="inline-flex rounded-full border border-navy/15 px-2.5 py-1 text-[11px] font-semibold text-navy hover:border-coral hover:text-coral"
              >
                Browse
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                shellResetRef.current?.();
                setAdjustRecommendationSummary(null);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full text-charcoal/55 hover:bg-charcoal/5 hover:text-charcoal focus:outline-none focus:ring-2 focus:ring-coral/40"
              aria-label="Start over"
              title="Start over"
            >
              <RotateCcw size={15} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={togglePanelExpanded}
              aria-label={panelExpanded ? "Shrink chat" : "Expand chat"}
              className="hidden md:flex h-9 w-9 items-center justify-center rounded-full text-charcoal/55 hover:bg-charcoal/5 hover:text-charcoal focus:outline-none focus:ring-2 focus:ring-coral/40"
            >
              {panelExpanded ? (
                <Minimize2 size={15} strokeWidth={2} />
              ) : (
                <Maximize2 size={15} strokeWidth={2} />
              )}
            </button>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              aria-label="Close chat"
              className="flex h-9 w-9 items-center justify-center rounded-full text-charcoal/55 hover:bg-charcoal/5 hover:text-charcoal focus:outline-none focus:ring-2 focus:ring-coral/40"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {adjustRecommendationSummary && celebrationContext && (
          <div
            className="shrink-0 border-b border-charcoal/10 bg-butter/25 px-4 py-2.5"
            data-testid="floating-adjust-summary"
          >
            <p className="text-[11px] font-medium uppercase tracking-wide text-charcoal/55">
              Current recommendation
            </p>
            <p className="truncate text-sm font-semibold text-navy">
              {adjustRecommendationSummary.productSummary}
            </p>
            <p className="text-xs text-charcoal/65">
              {adjustRecommendationSummary.bakery} ·{" "}
              {dollars(adjustRecommendationSummary.totalCents)}
            </p>
            <PersonalizationHintsStrip
              {...personalizationHints}
              compact
              className="mt-1.5 border-t border-charcoal/10 pt-1.5"
            />
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <InlineConcierge
            key={inlineConciergeKey}
            initialBrief={initialBrief}
            autoSendFirstMessage={Boolean(deepLinkInitialBrief?.raw_text)}
            onReset={() => {
              setCelebrationContext(null);
              setAdjustRecommendationSummary(null);
              setPanelOpen(false);
            }}
            cameFrom={
              isBrowseSurface ? "browse" : hasCelebrationCheckout ? "dashboard" : "landing"
            }
            chatSurface="floating"
            sessionPersistKey={FLOATING_CONCIERGE_SESSION_KEY}
            variant="floating"
            resetClosesSurface={false}
            shellResetRef={shellResetRef}
            onRecommendationCardsCount={handleRecommendationCardsCount}
            onDashboardClientActions={dashboardClientActions}
            onTeamClientActions={teamClientActions}
            celebrationId={celebrationContext?.celebrationId ?? null}
            checkoutDefaultDate={checkoutDefaultDate}
            checkoutDefaultAddress={checkoutDefaultAddress ?? undefined}
            includeInitialBriefRestore
            onResolvedBrief={handleResolvedBrief}
            activeDraftScope={activeDraftScope}
            loadActiveDraftFor={loadActiveDraftFor}
          />
        </div>
      </div>

      <ProductCheckoutModals
        flow={browseCheckout}
        recipientName={celebrationContext?.recipientName ?? ""}
        recipientId={celebrationContext?.recipientId ?? null}
        occasion={celebrationContext?.occasion ?? "other"}
        defaultDate={checkoutDefaultDate}
        defaultAddress={checkoutDefaultAddress ?? undefined}
        celebrationId={celebrationContext?.celebrationId ?? null}
      />

      {browseOverlay}
    </>
  );
}
